import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getChannels } from "../analyzeUtils";
import useGetSearchResult from "./useGetSearchResult";
import useGetSearchSummary from "./useGetSearchSummary";
import { dataSourceHandleActions, messageActions } from "_actions";
import useLocationQueryParams from "components/v2/hooks/useLocationQueryParams";

export default function useAnalyze() {
  const ROWS_PER_PAGE = 10;
  const currentUser = JSON.parse(localStorage.user);
  const organizationTrendsId = currentUser?._embedded?.organization?.trendsId;
  const { params, setParams } = useLocationQueryParams();
  const dispatch = useDispatch();
  const dataSourceHandles = useSelector(
    (state) => state.dataSourceHandles.dataSourceHandles,
  );
  const exporting = useSelector((state) => state.search?.exporting);
  const { wordCloud, loadingWordCloud } = useSelector((state) => state.search);

  const hasFetchedInitialWordCloud = useRef(false);

  const derivedParams = useMemo(() => {
    const start = params.startDate
      ? moment(params.startDate).startOf("day")
      : moment().startOf("day");

    const end = params.endDate ? moment(params.endDate).endOf("day") : moment();

    const channels = params.channels
      ? params.channels.split(",").map(Number)
      : [];

    return {
      startDate: start,
      endDate: end,
      channels,
      searchString: params.searchString || "",
      offset: Number(params.offset || 0),
      page: Number(params.page || 0),
      direction: params.direction || "INCOMING",
    };
  }, [params]);

  const { channelsList, channelIds } = useMemo(() => {
    if (!dataSourceHandles) return { channelsList: [], channelIds: [] };

    return {
      channelsList: getChannels(dataSourceHandles),
      channelIds: getChannels(dataSourceHandles, "ids"),
    };
  }, [dataSourceHandles]);

  const [searchInput, setSearchInput] = useState(
    () => derivedParams.searchString,
  );

  const [filterParams, setFilterParams] = useState(() => ({
    startDate: derivedParams.startDate.format("YYYY-MM-DD HH:mm:ss"),
    endDate: derivedParams.endDate.format("YYYY-MM-DD HH:mm:ss"),
    organization: [organizationTrendsId],
    channels: derivedParams.channels,
    offset: derivedParams.offset,
    direction: derivedParams.direction,
    page: derivedParams.page,
  }));

  const initialWordCloudDates = useRef({
    startDate: derivedParams.startDate.format("YYYY-MM-DD HH:mm:ss"),
    endDate: derivedParams.endDate.format("YYYY-MM-DD HH:mm:ss"),
  });

  const buildSearchParams = useCallback(
    ({ filters, searchString, page = 0, offset = 0 }) => {
      const channels =
        filters.channels?.length > 0
          ? filters.channels
          : getChannels(dataSourceHandles, "ids");

      return {
        organizations: filters.organization,
        channels,
        direction: filters.direction,
        searchString,
        startDate: moment(filters.startDate).startOf("day").toISOString(),
        endDate: moment(filters.endDate).endOf("day").toISOString(),
        offset,
        page,
        show: true,
      };
    },
    [dataSourceHandles],
  );

  const handleSearch = useCallback(
    (searchString) => {
      const trimmed = searchString?.trim();
      if (!trimmed) return;

      const nextFilters = {
        ...filterParams,
        page: 0,
        offset: 0,
      };

      setFilterParams(nextFilters);

      const requestParams = buildSearchParams({
        filters: nextFilters,
        searchString: trimmed,
        page: 0,
        offset: 0,
      });

      dispatch(messageActions.clearVisitedPages());
      dispatch(messageActions.searchMessages(requestParams));
      dispatch(messageActions.getSearchSummary(requestParams));
      setParams(requestParams);
    },
    [filterParams, buildSearchParams, dispatch, setParams],
  );

  const handleWordCloudWordClick = useCallback(
    (word) => {
      setSearchInput(word);
      handleSearch(word);
    },
    [handleSearch],
  );

  const handlePageChange = useCallback(
    (e, nextPage) => {
      const nextOffset = (nextPage || 0) * ROWS_PER_PAGE;

      const updatedFilters = {
        ...filterParams,
        page: nextPage,
        offset: nextOffset,
      };

      setFilterParams(updatedFilters);

      const requestParams = buildSearchParams({
        filters: updatedFilters,
        searchString: searchInput,
        page: nextPage,
        offset: nextOffset,
      });

      dispatch(messageActions.searchMessages(requestParams));
      setParams(requestParams);
    },
    [filterParams, searchInput, buildSearchParams, dispatch, setParams],
  );

  const buildWordCloudParams = useCallback(
    (startDate, endDate) => {
      const channels =
        channelIds?.length > 0
          ? channelIds
          : getChannels(dataSourceHandles, "ids");

      if (!organizationTrendsId || !channels?.length) return null;

      return {
        organizations: [organizationTrendsId],
        channels,
        direction: "ALL",
        startDate,
        endDate,
      };
    },
    [channelIds, dataSourceHandles, organizationTrendsId],
  );

  const fetchWordCloudData = useCallback(() => {
    if (!dataSourceHandles) return;

    const params = buildWordCloudParams(
      moment(filterParams.startDate).startOf("day").toISOString(),
      moment(filterParams.endDate).endOf("day").toISOString(),
    );

    if (params) dispatch(messageActions.getWordCloudData(params));
  }, [filterParams, buildWordCloudParams, dispatch, dataSourceHandles]);

  const fetchInitialWordCloudData = useCallback(() => {
    if (!dataSourceHandles || hasFetchedInitialWordCloud.current) return;

    const params = buildWordCloudParams(
      moment(initialWordCloudDates.current.startDate)
        .startOf("day")
        .toISOString(),
      moment(initialWordCloudDates.current.endDate).endOf("day").toISOString(),
    );

    if (params) {
      dispatch(messageActions.getWordCloudData(params));
      hasFetchedInitialWordCloud.current = true;
    }
  }, [buildWordCloudParams, dispatch, dataSourceHandles]);

  const handleSubmit = useCallback(() => {
    if (searchInput) {
      handleSearch(searchInput);
    } else {
      fetchWordCloudData();
    }
  }, [searchInput, handleSearch, fetchWordCloudData]);

  const handleExport = useCallback(() => {
    const exportParams = buildSearchParams({
      filters: filterParams,
      searchString: searchInput,
      page: 0,
      offset: 0,
    });

    dispatch(messageActions.exportAdminSearch(exportParams));
  }, [buildSearchParams, filterParams, searchInput, dispatch]);

  const handleTableCellClick = useCallback((tableParams) => {
    if (tableParams) window.location.replace(tableParams);
  }, []);

  const handleSearchField = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleSearchOnKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") handleSubmit();
    },
    [handleSubmit],
  );

  useEffect(() => {
    fetchInitialWordCloudData();
  }, [fetchInitialWordCloudData]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const id = user?._embedded?.organization?.id;

    if (id) {
      dispatch(
        dataSourceHandleActions.getAll({
          organization: id,
        }),
      );
    }
  }, [dispatch]);

  const { rows, columns, totalCount, loading } = useGetSearchResult();
  const { searchSummary, loadingSummary } = useGetSearchSummary();

  const hasSearchResults = rows.length > 0;
  const hasSummary = Boolean(searchSummary?.data?.response);
  const showWordCloud = !loading && !hasSearchResults && !hasSummary;
  const showWordCloudLoading =
    loadingWordCloud || !hasFetchedInitialWordCloud.current;

  return {
    channelsList,
    channelIds,
    filterParams,
    setFilterParams,
    searchInput,
    handleSearchField,
    handleSearchOnKeyDown,
    handleSubmit,
    loadingSummary,
    searchSummary,
    hasSearchResults,
    totalCount,
    exporting,
    handleExport,
    loading,
    rows,
    columns,
    handlePageChange,
    handleTableCellClick,
    showWordCloud,
    wordCloud,
    showWordCloudLoading,
    handleWordCloudWordClick,
  };
}
